import React, { useState, useEffect } from 'react';

// Hook to detect screen size
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 768 : false,
    isTablet: typeof window !== 'undefined' ? window.innerWidth >= 768 && window.innerWidth < 1024 : false,
    isDesktop: typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      setScreenSize({
        width,
        height,
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      });
    };

    // Debounce resize events for better performance
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, []);

  return screenSize;
};

/**
 * ResponsiveLayout - Main layout wrapper with responsive behavior
 * @param {Object} props
 * @param {React.ReactNode} props.children - Main content
 * @param {React.ReactNode} props.sidebar - Sidebar content
 * @param {React.ReactNode} props.header - Header content
 * @param {boolean} props.sidebarOpen - Whether sidebar is open (mobile)
 * @param {Function} props.onSidebarToggle - Sidebar toggle handler
 */
export const ResponsiveLayout = ({
  children,
  sidebar,
  header,
  sidebarOpen = false,
  onSidebarToggle,
  className = ''
}) => {
  const { isMobile } = useResponsive();

  return (
    <div className={`min-h-screen bg-white ${className}`}>
      {/* Header */}
      {header && (
        <header className="sticky top-0 z-40 w-full bg-white/80 backdrop-blur-sm border-b border-gray-200">
          <div className="container mx-auto">
            {header}
          </div>
        </header>
      )}

      <div className="flex min-h-[calc(100vh-4rem)]">
        {/* Sidebar */}
        {sidebar && (
          <>
            {/* Mobile sidebar overlay */}
            {isMobile && sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50 lg:hidden"
                onClick={onSidebarToggle}
              />
            )}

            {/* Sidebar content */}
            <aside className={`
              ${isMobile
                ? `fixed left-0 top-16 bottom-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
                : 'relative w-64 flex-shrink-0'
              }
              bg-white border-r border-gray-200 overflow-y-auto
            `}>
              <div className="p-4">
                {sidebar}
              </div>
            </aside>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-x-hidden">
          <div className="container mx-auto py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

/**
 * ResponsivePageHeader - Page header component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Page subtitle
 * @param {React.ReactNode} props.actions - Header actions
 * @param {React.ReactNode} props.breadcrumb - Breadcrumb component
 */
export const ResponsivePageHeader = ({
  title,
  subtitle,
  actions,
  breadcrumb,
  className = ''
}) => (
  <div className={`mb-6 sm:mb-8 ${className}`}>
    {breadcrumb && (
      <div className="mb-2 text-sm text-gray-500">
        {breadcrumb}
      </div>
    )}

    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-base sm:text-lg text-gray-600">
            {subtitle}
          </p>
        )}
      </div>

      {actions && (
        <div className="flex flex-wrap gap-2 sm:gap-3">
          {actions}
        </div>
      )}
    </div>
  </div>
);

/**
 * ResponsiveSection - Content section wrapper
 * @param {Object} props
 * @param {React.ReactNode} props.children - Section content
 * @param {string} props.title - Section title
 * @param {string} props.spacing - Spacing size ('sm', 'md', 'lg')
 */
export const ResponsiveSection = ({
  children,
  title,
  spacing = 'md',
  className = ''
}) => {
  const spacingClasses = {
    'sm': 'mb-4 sm:mb-6',
    'md': 'mb-6 sm:mb-8',
    'lg': 'mb-8 sm:mb-12'
  };

  return (
    <section className={`${spacingClasses[spacing]} ${className}`}>
      {title && (
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
};

export default ResponsiveLayout;